import type { VsmContext, PageConfigEntry, PageConfigFn } from '@wix/thunderbolt-vsm-api-types'
import type { AnimationsPageConfig } from '../types'
import type { PageData } from '@wix/thunderbolt-becky-types'
import { name } from '../symbols'
import { getFullId } from '@wix/thunderbolt-commons'

type PageBackgroundRef = { scrollType?: string; mediaType?: string; type?: string }
type PageDataWithBackgrounds = PageData & {
	pageBackgrounds?: {
		mobile?: { ref?: PageBackgroundRef }
		desktop?: { ref?: PageBackgroundRef }
	}
}

function isImageMedia(media: PageBackgroundRef | undefined): boolean {
	if (!media) return false
	const type = media.mediaType ?? media.type
	return type === 'Image' || type === 'WixVideo'
}

function backgroundHasParallax(pageData: PageData | undefined, isMobileView: boolean): boolean {
	if (!pageData) return false
	const viewMode = isMobileView ? 'mobile' : 'desktop'
	const bgRef = (pageData as PageDataWithBackgrounds).pageBackgrounds?.[viewMode]?.ref
	if (!bgRef) return false
	const scrollType = bgRef.scrollType ?? 'scroll'
	return scrollType === 'parallax' && isImageMedia(bgRef)
}

const animationsPageConfig: PageConfigFn<AnimationsPageConfig> = (
	{ api, activeFeatures }: VsmContext,
	pageId: string
) => {
	const isInSeo = api.env.isInSeo()
	if (isInSeo) {
		return { shouldLoadPageFeature: false }
	}

	const isMobileView = api.env.isMobileView()
	const viewMode = api.env.getViewMode()

	const pageBackgrounds = api.components.getComponentsByType('PageBackground')
	let hasParallaxBg = false
	for (const pbId of Object.keys(pageBackgrounds)) {
		const bgPageId = pbId.replace('pageBackground_', '')
		const bgPageData = api.page.getPageData(bgPageId)
		if (backgroundHasParallax(bgPageData, isMobileView)) {
			hasParallaxBg = true
			break
		}
	}

	const hasAnimations = api.components.hasComponentWithPredicate((_, compId) => {
		return api.components.getAnimationsByViewMode(compId, viewMode!).length > 0
	})

	const hasPlatform = api.platform.hasPlatformOnPage()
	const sliderGalleryActive = activeFeatures.isActive('sliderGallery')

	const shouldLoad = hasAnimations || hasParallaxBg || hasPlatform || sliderGalleryActive
	if (!shouldLoad) {
		return { shouldLoadPageFeature: false }
	}

	const isPreview = api.env.getRenderFlag<string>('componentViewMode') === 'preview'

	const ancestors = api.components.getAncestors()
	const compsWithEffects = api.components.getCompsWithEffectsForPage(pageId)
	const repeaterTemplateToParentMap: Record<string, string> = {}

	for (const compId of Object.keys(compsWithEffects)) {
		const templateId = getFullId(compId)
		if (repeaterTemplateToParentMap[templateId]) continue
		const compAncestors = ancestors[compId]
		if (!compAncestors) continue
		for (const ancestorId of compAncestors) {
			const ancestorType = api.components.getCompType(ancestorId)
			if (ancestorType?.endsWith('Repeater') || api.components.hasTemplateContainer(ancestorId)) {
				repeaterTemplateToParentMap[templateId] = ancestorId
				break
			}
		}
	}

	return {
		shouldLoadPageFeature: true,
		pageConfig: {
			repeaterTemplateToParentMap,
			isPreview,
		},
	}
}

export const pageConfig: PageConfigEntry<AnimationsPageConfig> = {
	name,
	configFactory: animationsPageConfig,
	experiment: 'specs.thunderbolt.vsmPageConfigs',
}
